import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { slideReveal, popIn, shimmer } from "./Autopilot.styled";
import { SectionWithSlides } from "hooks/useStoryboardWithSlides/";
import { StreamingSlidePreview } from "pages/media/projects/components/slides/";
import type { Templates } from "pages/media/components/storyboardEditor/types/";
import { useBrandKit } from "hooks/useBrandKit/";
import { radius } from "@libertify/ui";

const SLIDE_WIDTH = 120;

interface ScenesVisualProps {
  isActive: boolean;
  isDone: boolean;
  onComplete: () => void;
  sections: SectionWithSlides[];
  templates: Templates;
  brandKitData: ReturnType<typeof useBrandKit>;
  mediaAspectRatio: number;
  autoScrollAllowed?: { readonly current: boolean };
}

interface SlideItem {
  slideId: string;
  slide: SectionWithSlides["slides"][number];
}

interface SlideSlotProps {
  slideItem: SlideItem;
  templates: Templates;
  brandKitData: ReturnType<typeof useBrandKit>;
  templateSize: { width: number; height: number };
  revealDelay: number;
  isDone: boolean;
  onReveal: () => void;
}

const SlideSlot: React.FC<SlideSlotProps> = ({
  slideItem,
  templates,
  brandKitData,
  templateSize,
  revealDelay,
  isDone,
  onReveal,
}) => {
  const [revealed, setRevealed] = useState(isDone);
  const onRevealRef = useRef(onReveal);
  useEffect(() => {
    onRevealRef.current = onReveal;
  });

  const startTimeRef = useRef(0);
  const revealedRef = useRef(isDone);

  const doReveal = useCallback(() => {
    if (revealedRef.current) return;
    revealedRef.current = true;
    setRevealed(true);
    onRevealRef.current();
  }, []);

  useEffect(() => {
    if (isDone) return;
    startTimeRef.current = Date.now();
    const t = setTimeout(doReveal, revealDelay);

    // Catch up if the tab was backgrounded and the timeout was throttled
    const onVisible = () => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - startTimeRef.current >= revealDelay
      ) {
        doReveal();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearTimeout(t);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [revealDelay, isDone, doReveal]);

  const template = templates.get(slideItem.slideId);
  const isVisible = isDone || revealed;

  if (!isVisible || !template) return <SceneShim style={templateSize} />;

  return (
    <SlideWrapper style={templateSize}>
      <StreamingSlidePreview
        templateSize={templateSize}
        slide={slideItem.slide}
        template={template}
        brandKitData={brandKitData}
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onCardClick={() => {}}
        border={{ borderWidth: 0, borderRadius: radius.box }}
        compact
      />
    </SlideWrapper>
  );
};

export const ScenesVisual: React.FC<ScenesVisualProps> = ({
  isActive,
  isDone,
  onComplete,
  sections,
  templates,
  brandKitData,
  mediaAspectRatio,
  autoScrollAllowed,
}) => {
  const revealCount = useRef(0);
  const [visibleCount, setVisibleCount] = useState(0);
  const [allRevealed, setAllRevealed] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  });

  const slides = useMemo((): SlideItem[] => {
    return sections.flatMap((section) =>
      section.slides.map((slide) => ({ slideId: slide.uuid, slide }))
    );
  }, [sections]);

  const templateSize = useMemo(
    () => ({
      width: SLIDE_WIDTH,
      height: Math.round(SLIDE_WIDTH / mediaAspectRatio),
    }),
    [mediaAspectRatio]
  );

  const handleReveal = useCallback(() => {
    revealCount.current += 1;
    setVisibleCount(revealCount.current);
    if (revealCount.current >= slides.length) setAllRevealed(true);
  }, [slides.length]);

  // Scroll as slides reveal — stops when all are visible
  useEffect(() => {
    if (
      isActive &&
      visibleCount > 0 &&
      visibleCount < slides.length &&
      autoScrollAllowed?.current !== false
    ) {
      bottomRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [isActive, visibleCount, slides.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Complete after the render where all slides are displayed
  useEffect(() => {
    if (allRevealed) onCompleteRef.current();
  }, [allRevealed]);

  if (!isActive && !isDone) return null;
  if (slides.length === 0) return null;

  return (
    <ScenesRowV2>
      {slides.map((slideItem, i) => {
        if (!isDone && i > visibleCount) return null;
        return (
          <SlideSlot
            key={slideItem.slideId}
            slideItem={slideItem}
            templates={templates}
            brandKitData={brandKitData}
            templateSize={templateSize}
            revealDelay={i * 150}
            isDone={isDone}
            onReveal={handleReveal}
          />
        );
      })}
      <div ref={bottomRef} />
    </ScenesRowV2>
  );
};

const ScenesRowV2 = styled.div`
  padding: 14px 15px 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  animation: ${slideReveal} 0.3s ease both;
`;

const SceneShim = styled.div`
  border-radius: ${radius.box}px;
  flex-shrink: 0;
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
  background-size: 400px 100%;
  animation: ${shimmer} 1.4s linear infinite;
`;

const SlideWrapper = styled.div`
  flex-shrink: 0;
  border-radius: ${radius.box}px;
  overflow: hidden;
  animation: ${popIn} 0.35s ease both;
`;
